import { Injectable } from '@angular/core';
import {
	PluginType,
	PluginWordGuessGameData,
	WordGuessLevel,
	HintMode,
} from '@meta-press/shared-plugins';
import {
	GenerationRequest,
	GenerationResult,
	GenerationPrompts,
	ValidationResult,
} from './base/game-generation.interface';
import { BaseGenerationService } from './base/base-generation.service';
import { SettingsService } from '../settings-service/settings.service';

/**
 * Word guess specific generation service
 */
@Injectable({
	providedIn: 'root',
})
export class WordGuessGenerationService extends BaseGenerationService<PluginWordGuessGameData> {
	readonly pluginType = PluginType.WordGuess;

	/**
	 * Extract word content for auto-generation
	 * @param sourceMaterial The source material to analyze
	 * @param settingsService Settings service for language preferences
	 * @param parameters Generation parameters including level settings
	 * @returns Prompts for automatic word extraction
	 */
	extractWordContent(
		sourceMaterial: any,
		settingsService: SettingsService,
		parameters?: any,
	): GenerationPrompts {
		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();

		// Extract level configuration from parameters
		const numberOfLevels = parameters?.['numberOfLevels'] || 3;
		const levelLetterCounts = parameters?.['levelLetterCounts'] || [
			'long',
			'short',
			'short',
			'short',
			'short',
		];
		const wordsPerLevel = parameters?.['wordsPerLevel'] || [1, 2, 3, 4, 5];
		const generateHints = parameters?.['generateHints'] ?? true;

		// Get difficulty level for hint generation
		const difficultyName =
			settingsService.GenerationDifficulty || 'Keskivaikea';
		const difficultyLevel =
			difficultyName === 'Helppo'
				? 'Helppo'
				: difficultyName === 'Vaikea'
					? 'Vaikea'
					: 'Keskitaso';

		const systemPrompt = `Olet sanapelin luoja. Tehtäväsi on analysoida lähdemateriaali ja poimia siitä sopivia sanoja sanapeliä varten. Keskity tärkeimpiin käsitteisiin ja termeihin materiaalissa.${generateHints ? ' Luo myös vihjeitä sanoille vaikeustason mukaan.' : ''} Vastaa aina validoidussa JSON-muodossa. Et saa vastata muussa muodossa.

${targetLanguagePrompt}`;

		const includeImages = sourceMaterial?.imagesAsString
			? `\n\nLähdemateriaalissa on myös kuvia. Käytä kuvia apuna sanojen valinnassa:\n${sourceMaterial.imagesAsString}`
			: '';

		// Create level specifications based on parameters
		let levelSpecifications = '';
		// const categories = [
		// 	'perusasiat',
		// 	'edistyneet käsitteet',
		// 	'erikoistermit',
		// 	'harvinaiset sanat',
		// 	'lyhyet sanat',
		// ];

		for (let i = 0; i < numberOfLevels; i++) {
			const letterCount = this.getArrayValue(levelLetterCounts, i);
			const letterCountFinnish = this.convertLengthToFinnish(letterCount);
			const wordCount = this.getArrayValue(wordsPerLevel, i);

			levelSpecifications += `- Taso ${i + 1}: ${wordCount} sana(a). Etsi monta vaihtoehtoa, jotka ovat ${letterCount == 'short' ? 'lyhyitä' : 'pitkiä'} sanoja. Valitse niistä ${letterCount == 'short' ? 'lyhin' : 'pisin'} sana.\n`;
		}

		const userPrompt = `Analysoi annettu lähdemateriaali ja poimi siitä sopivia sanoja sanapeliä varten.${includeImages}

Luo ${numberOfLevels} tasoa sanoja seuraavien vaatimusten mukaan:
${levelSpecifications}. Sanojen tulee olla suoraan lähdemateriaalista sellaisenaan. Voit kuitenkin muuntaa sanan perusmuotoon.

1. Etsi lähdemateriaalista sen käsittelemät pääteemat ja aiheet. Kategorian tulee olla yksi selkeä sana, joka kuvaa tasolla olevia sanoja (esim. "Eläimet", "Ruoka", "Ammatit", "Kaupungit")
2. Etsi lähdemateriaalista tason vaatima määrä sanoja, jotka sopivat teemaan.
* Sanan tulee olla perusmuodossa
* Se ei saa sisältää erikoismerkkejä eikä numeroita
3. Kun olet valinnut tasolle valitut sanat, päättele sitten tason teema,. Kaikkien sanojen on sovittava teemaan. Jokaisella tasolla tulisi olla eri teema. On tärkeää, että jokainen sana sopii valittuun teemaan eli tason aihealueeseen.


KRIITTINEN SÄÄNTÖ - Kategorian ja sanojen yhteensopivuus:
* Jokaisen sanan TÄYTYY kuulua valittuun kategoriaan
* Esimerkki OIKEIN: Kategoria "Ammatit" → sanat LÄÄKÄRI, OPETTAJA, INSINÖÖRI (kaikki ammatteja)
* Esimerkki VÄÄRIN: Kategoria "Ammatit" → sanat TALO, AURINKO, PALLO (eivät ole ammatteja)
* Tarkista jokainen sana erikseen: Sopiiko se kategoriaan? Jos ei, ÄLÄ käytä sitä
* Jos et löydä tarpeeksi sopivia sanoja kategoriaan, VAIHDA kategoriaa toiseksi josta löydät riittävästi sanoja
* Viimeinen tarkistus ennen vastaamista: Lue kategoria ja kaikki sen sanat - sopivatko ne kaikki yhteen?
${
	generateHints
		? `
Vihjetekstien luominen (vaikeustaso: ${difficultyLevel}):
- Helppo: Suorat ja selkeät vihjeet (esim. "Kotieläin joka haukkuu")
- Keskitaso: Hieman epäsuoremmat vihjeet (esim. "Ihmisen uskollinen kumppani")
- Vaikea: Abstraktit tai metaforiset vihjeet (esim. "Suden perillinen")
- Luo jokaiselle sanalle sopiva vihjeteksti valitun vaikeustason mukaan
- Vihjeet eivät saa paljastaa sanaa suoraan
- Vihjeet tulee olla lyhyitä (max 8 sanaa)`
		: ''
}

Vastaa JSON-muodossa:
{
	"gameTitle": "Pelin nimi (lyhyt ja kuvaava)",
	"levels": [
		{
			"category": "kategoria tai teema",
			"words": ["SANA1"]${generateHints ? ',\n\t\t\t"hintTexts": ["Vihjeteksti sanalle SANA1"]' : ''}
		},
		{
			"category": "eri kategoria",
			"words": ["SANA2", "SANA3"]${generateHints ? ',\n\t\t\t"hintTexts": ["Vihjeteksti sanalle SANA2", "Vihjeteksti sanalle SANA3"]' : ''}
		}
		// ... ${numberOfLevels} tasoa yhteensä
	],
	"defaultLevel": 1
}`;

		return { system: systemPrompt, user: userPrompt };
	}

	/**
	 * Create prompts for custom word generation with user input
	 * @param request The generation request
	 * @param settingsService Settings service for language preferences
	 * @returns System and user prompts
	 */
	createCustomWordPrompts(
		request: GenerationRequest,
		settingsService: SettingsService,
	): GenerationPrompts {
		const { parameters } = request;
		const editRequest = parameters['editRequest'] || '';

		// Extract level configuration from parameters
		const numberOfLevels = parameters?.['numberOfLevels'] || 3;
		const levelLetterCounts = parameters?.['levelLetterCounts'] || [
			'long',
			'short',
			'short',
			'short',
			'short',
		];
		const wordsPerLevel = parameters?.['wordsPerLevel'] || [1, 2, 3, 4, 5];
		const generateHints = parameters?.['generateHints'] ?? true;

		// Get difficulty level for hint generation
		const difficultyName =
			settingsService.GenerationDifficulty || 'Keskivaikea';
		const difficultyLevel =
			difficultyName === 'Helppo'
				? 'Helppo'
				: difficultyName === 'Vaikea'
					? 'Vaikea'
					: 'Keskitaso';

		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();

		const systemPrompt = `Olet sanapelin luoja. Tehtäväsi on luoda sanoja annetun aiheen pohjalta. Luo sopivia sanoja ja sanapareja sanapeliin.${generateHints ? ' Luo myös vihjeitä sanoille vaikeustason mukaan.' : ''} Palauta vastaus aina validoitussa JSON-muodossa. Et saa vastata muussa muodossa.

${targetLanguagePrompt}`;

		// Create level specifications based on parameters
		let levelSpecifications = '';
		// const categories = [
		// 	'perusasiat',
		// 	'edistyneet käsitteet',
		// 	'erikoistermit',
		// 	'harvinaiset sanat',
		// 	'lyhyet sanat',
		// ];

		for (let i = 0; i < numberOfLevels; i++) {
			const letterCount = this.getArrayValue(levelLetterCounts, i);
			const letterCountFinnish = this.convertLengthToFinnish(letterCount);
			const wordCount = this.getArrayValue(wordsPerLevel, i);

			levelSpecifications += `- Taso ${i + 1}: ${wordCount} sana(a). Etsi monta vaihtoehtoa, jotka ovat ${letterCount == 'short' ? 'lyhyitä' : 'pitkiä'} sanoja. Valitse niistä ${letterCount == 'short' ? 'lyhin' : 'pisin'} sana.\n`;
		}

		const userPrompt = `Käyttäjän aihe tai pyyntö on: ${editRequest}.

Luo ${numberOfLevels} tasoa sanoja annetun aiheen pohjalta.

Tasovaatimukset:
${levelSpecifications}

Yleiset säännöt:
- Valitse aiheeseen sopivia sanoja
- Sanat tulee olla isoja kirjaimia
- Vältä erikoismerkkejä ja numeroita
- Jokaisella tasolla tulee olla eri kategoria/alakategoria aiheesta
- Vaikeammat/pidemmät sanat ensimmäisille tasoille, lyhyemmät ja helpommat viimeisinä
- Kategorian tulee olla yksi selkeä sana, joka kuvaa tasolla olevia sanoja (esim. "Eläimet", "Ruoka", "Ammatit", "Kaupungit")

KRIITTINEN SÄÄNTÖ - Kategorian ja sanojen yhteensopivuus:
* Jokaisen sanan TÄYTYY kuulua valittuun kategoriaan
* Esimerkki OIKEIN: Kategoria "Ammatit" → sanat LÄÄKÄRI, OPETTAJA, INSINÖÖRI (kaikki ammatteja)
* Esimerkki VÄÄRIN: Kategoria "Ammatit" → sanat TALO, AURINKO, PALLO (eivät ole ammatteja)
* Tarkista jokainen sana erikseen: Sopiiko se kategoriaan? Jos ei, ÄLÄ käytä sitä
* Jos et löydä tarpeeksi sopivia sanoja kategoriaan, VAIHDA kategoriaa toiseksi josta löydät riittävästi sanoja
* Viimeinen tarkistus ennen vastaamista: Lue kategoria ja kaikki sen sanat - sopivatko ne kaikki yhteen?
${
	generateHints
		? `
Vihjetekstien luominen (vaikeustaso: ${difficultyLevel}):
- Helppo: Suorat ja selkeät vihjeet (esim. "Kotieläin joka haukkuu")
- Keskitaso: Hieman epäsuoremmat vihjeet (esim. "Ihmisen uskollinen kumppani")
- Vaikea: Abstraktit tai metaforiset vihjeet (esim. "Koti on siellä missä tämä on")
- Luo jokaiselle sanalle sopiva vihjeteksti valitun vaikeustason mukaan
- Vihjeet eivät saa paljastaa sanaa suoraan
- Vihjeet tulee olla lyhyitä (max 8 sanaa)`
		: ''
}

Vastaa JSON-muodossa:
{
	"gameTitle": "Pelin nimi aiheeseen sopiva",
	"levels": [
		{
			"category": "kategoria tai teema",
			"words": ["SANA1"]${generateHints ? ',\n\t\t\t"hintTexts": ["Vihjeteksti sanalle SANA1"]' : ''}
		},
		{
			"category": "eri kategoria",
			"words": ["SANA2", "SANA3"]${generateHints ? ',\n\t\t\t"hintTexts": ["Vihjeteksti sanalle SANA2", "Vihjeteksti sanalle SANA3"]' : ''}
		}
		// ... ${numberOfLevels} tasoa yhteensä
	],
	"defaultLevel": 1
}`;

		return { system: systemPrompt, user: userPrompt };
	}

	/**
	 * Create system and user prompts for word guess generation
	 * @param request The generation request
	 * @param settingsService Settings service for language preferences
	 * @returns System and user prompts
	 */
	createPrompts(
		request: GenerationRequest,
		settingsService: SettingsService,
	): GenerationPrompts {
		const { parameters } = request;

		// For auto-generation, use automatic word extraction
		if (parameters['autoGeneration']) {
			return this.extractWordContent(
				request.sourceMaterial,
				settingsService,
				parameters,
			);
		}

		// For custom word generation with user input
		if (parameters['customGeneration']) {
			return this.createCustomWordPrompts(request, settingsService);
		}

		throw new Error('Generation method not yet implemented');
	}

	/**
	 * Process AI response into PluginWordGuessGameData
	 * @param response The AI service response
	 * @param parameters Original generation parameters
	 * @returns Generation result with game data or error
	 */
	protected processResponseCore(
		response: any,
		parameters: any,
	): GenerationResult {
		try {
			const responseData = response.json || {};

			// Handle both new level-based format and legacy format
			let levels: WordGuessLevel[] = [];
			let gameTitle = responseData.gameTitle;
			let defaultLevel = responseData.defaultLevel || 1;

			// Check for new format first
			if (
				Array.isArray(responseData.levels) &&
				responseData.levels.length > 0
			) {
				// New format with direct levels array
				levels = responseData.levels.map(
					(level: any, index: number) => ({
						category: level.category || '',
						words: Array.isArray(level.words) ? level.words : [],
						hintTexts: Array.isArray(level.hintTexts)
							? level.hintTexts
							: undefined,
					}),
				);
			} else {
				// Legacy format - convert to new format
				const { level1Words, level2WordPairs } = responseData;

				if (!level1Words && !level2WordPairs) {
					return {
						success: false,
						error: 'Invalid response format: no words found',
						rawResponse: response.rawResponse,
					};
				}

				// Create level 1 from level1Words
				if (Array.isArray(level1Words) && level1Words.length > 0) {
					levels.push({
						category: 'Perusasiat',
						words: level1Words,
					});
				}

				// Create level 2 from level2WordPairs
				if (
					Array.isArray(level2WordPairs) &&
					level2WordPairs.length > 0
				) {
					const level2Words: string[] = [];
					level2WordPairs.forEach((pair: any) => {
						if (pair.word1) level2Words.push(pair.word1);
						if (pair.word2) level2Words.push(pair.word2);
					});
					levels.push({
						category: 'Edistyneet käsitteet',
						words: level2Words,
					});
				}
			}

			// Fallback to default levels if no valid data
			if (levels.length === 0) {
				levels.push(
					{
						category: 'Perusasiat',
						words: ['KOIRA'],
					},
					{
						category: 'Edistyneet',
						words: ['AURINKO', 'KELLO'],
					},
				);
			}

			// Create game data with generated words
			const gameData = new PluginWordGuessGameData();

			if (gameTitle) {
				gameData.gameTitle = gameTitle;
			}

			// Set word configuration with new structure
			gameData.wordConfiguration = {
				levels: levels,
				defaultLevel: defaultLevel,
				completionSettings: {
					showProgressBar: true,
					showEndScreen: true,
					endScreenTitle: 'Onneksi olkoon!',
					endScreenText: 'Läpäisit kaikki tasot mestarillisesti!',
					playAgainButtonText: 'Pelaa uudelleen',
				},
				hintSettings: {
					mode: parameters['hintMode'] || HintMode.Letters,
					totalHints: 5,
				},
			};

			// Migrate legacy data to ensure compatibility
			gameData.migrateLegacyData();

			return {
				success: true,
				gameData,
				rawResponse: response.rawResponse,
			};
		} catch (error) {
			return {
				success: false,
				error: `Response processing failed: ${error}`,
				rawResponse: response.rawResponse,
			};
		}
	}

	/**
	 * Validate word guess specific request
	 */
	validateRequest(request: GenerationRequest): ValidationResult {
		const hasSourceMaterial =
			request.sourceMaterial &&
			request.sourceMaterial.fileHandles.length > 0;

		// For custom generation, we need edit request
		if (request.parameters['customGeneration']) {
			const hasEditRequest =
				request.parameters['editRequest'] &&
				request.parameters['editRequest'].trim();

			if (!hasEditRequest) {
				return {
					valid: false,
					error: 'Anna aihe tai kuvaus sanapelille',
				};
			}
		} else if (!hasSourceMaterial) {
			// For auto-generation, we need source material
			return {
				valid: false,
				error: 'Valitse vähintään yksi artikkeli lähteeksi',
			};
		}

		return { valid: true };
	}

	/**
	 * Get default parameters for word guess
	 */
	getDefaultParameters(): any {
		return {
			autoGeneration: true,
			customGeneration: false,
			editRequest: '',
			clearPrevious: true,
			numberOfLevels: 3,
			levelLetterCounts: ['long', 'short', 'short', 'short', 'short'], // Word length per level (English descriptors: 'short', 'medium', 'long')
			wordsPerLevel: [1, 2, 3, 4, 5], // Number of words per level
			questionTopic: '',
			hintMode: HintMode.Letters, // Default hint mode for generated games
			generateHints: true, // Whether to generate hint texts (only relevant for Verbal mode)
		};
	}

	/**
	 * Helper method to safely access array values with boundary checking
	 * Returns the last element if index exceeds array length
	 */
	private getArrayValue(array: string[], index: number): string {
		if (!array || array.length === 0) return 'short'; // Default fallback
		return array[Math.min(index, array.length - 1)];
	}

	/**
	 * Convert English length descriptor to Finnish
	 */
	private convertLengthToFinnish(length: string): string {
		switch (length) {
			case 'short':
				return 'lyhyitä';
			case 'medium':
				return 'keskipitkiä';
			case 'long':
				return 'pitkiä';
			default:
				return 'keskipitkiä';
		}
	}
}
